// frontend/src/content/guide.js
//
// The curriculum behind /guide. Task-oriented and role-gated: you see what you
// can actually do, in the order it has to happen.
//
// Tasks carry a `moduleKey`, NOT a module name or route — those come from
// modules.js, so a rename lands here for free and program gating is derived
// rather than duplicated. guide.test.js fails on a stale moduleKey, an unknown
// `requires` id, or a readinessKey the API no longer sends.
//
// Content is code, reviewed like code. Keep `why` to the reason the step exists
// (not a restatement of the title) and `steps` imperative, using the real UI
// labels a person will see on screen.
import { moduleByKey } from './modules';

export const PHASES = [
  { key: 'setup',
    title: 'Setting up',
    blurb: 'One-off, and the order matters — each step depends on the one before it.' },
  { key: 'cycle',
    title: 'Each cycle',
    blurb: 'The rhythm that repeats three times a year.' },
  { key: 'ongoing',
    title: 'Anytime',
    blurb: 'Work that is not tied to a cycle deadline.' },
];

const ALL = ['admin', 'dept_head', 'manager', 'employee'];
const MANAGES = ['admin', 'dept_head', 'manager'];

export const TASKS = [
  // ── SETUP ────────────────────────────────────────────────────────────────
  {
    id: 'create-program', phase: 'setup', title: 'Create a performance program',
    roles: ['admin'], moduleKey: 'departments',
    why: 'A program is a self-contained workflow — its own role types, KPI templates, and review cycles. Two ship already, so you only need this when starting something genuinely separate.',
    steps: [
      'Open Departments and find the Programs section.',
      'Click + Add program, name it, and describe what it covers.',
    ],
  },
  {
    id: 'add-department', phase: 'setup', title: 'Add a department',
    roles: ['admin'], moduleKey: 'departments', requires: ['create-program'],
    readinessKey: 'departments',
    why: 'A department binds people to a program. It is the only way a user gets one — there is no per-user program setting.',
    steps: [
      'Open Departments and click + Add department.',
      'Pick the performance program it belongs to.',
      'Everyone you put in this department inherits that program’s role types, cycles, and KPI templates.',
    ],
  },
  {
    id: 'define-role-types', phase: 'setup', title: 'Define role types',
    roles: ['admin', 'dept_head'], moduleKey: 'role-types', requires: ['add-department'],
    readinessKey: 'role_types',
    why: 'A role type is what decides which KPI templates a person receives. It is data, not a fixed list, so each program defines its own.',
    steps: [
      'Open Role types and click + Add role type.',
      'Set the key (stored value) and a display label.',
      'Set the quarterly reward and whether the role can be a champion, if the program uses those.',
    ],
  },
  {
    id: 'add-people', phase: 'setup', title: 'Add people',
    roles: ['admin', 'dept_head'], moduleKey: 'people', requires: ['define-role-types'],
    readinessKey: 'users',
    why: 'Every person needs a department (which gives them a program) and a role type (which gives them KPIs). Skip the role type and they will silently have no KPIs at all.',
    steps: [
      'Open Manage users and click + Add user.',
      'Set their role: Employee, Manager, Department Head, or Admin.',
      'Set who they report to — this drives what their manager can see and score.',
      'Set their department, then pick a role type from that program’s list.',
    ],
  },
  {
    id: 'open-review-cycle', phase: 'setup', title: 'Open a review cycle',
    roles: ['admin', 'dept_head'], moduleKey: 'review-cycles', requires: ['add-department'],
    readinessKey: 'cycle',
    why: 'A cycle is the period everything is measured against. KPI templates, reviews, and OKRs all hang off one.',
    steps: [
      'Open Review cycles and click + New cycle.',
      'Set the period, the cycle number (1–3), and the year. Mark cycle 3 as annual so it triggers bonus generation.',
      'Optionally set the three milestone dates — the system emails a reminder three days before each.',
      'Leave “Carry forward KPIs from the previous cycle” ticked to bring last cycle’s plans and weights across.',
    ],
  },
  {
    id: 'define-kpi-templates', phase: 'setup', title: 'Define KPI templates',
    roles: ['admin', 'dept_head'], moduleKey: 'kpi-setup', requires: ['open-review-cycle', 'define-role-types'],
    readinessKey: 'kpi_templates',
    why: 'Templates are the KPIs a role is measured on, per cycle. Without them nobody on that role gets anything to score.',
    steps: [
      'Open KPI setup and switch to the KPI templates tab.',
      'If a previous cycle has the right set, use “Copy KPIs from previous cycle” instead of retyping it.',
      'Otherwise click + KPI template: title, category, the role type it applies to, and a weight.',
      'Weights for one person must total 100% across all their KPIs.',
    ],
  },

  // ── EACH CYCLE ───────────────────────────────────────────────────────────
  {
    id: 'kpis-auto-assign', phase: 'cycle', title: 'KPIs assign themselves',
    roles: MANAGES, moduleKey: 'kpi-setup',
    why: 'You do not assign KPIs person by person. Anyone whose role type matches a template gets it automatically the first time the page is loaded.',
    steps: [
      'Check KPI setup to see each person’s plan and that their weights total 100%.',
      'Use Reassign to reset someone to their role’s canonical set, or Archive to clear their plan.',
      'If someone shows no KPIs, the page now tells you which step is missing.',
    ],
  },
  {
    id: 'self-evaluate', phase: 'cycle', title: 'Score yourself against your KPIs',
    roles: ALL, moduleKey: 'kpi-scoring',
    why: 'Your self-evaluation is the first half of your score. Your manager sees it before adding theirs.',
    steps: [
      'Open KPI scoring and rate each KPI 1–5 against its target.',
      'Add a comment explaining the rating — this is what the conversation is built on.',
      'You can keep editing until your manager finalises it.',
    ],
  },
  {
    id: 'manager-score', phase: 'cycle', title: 'Score your team',
    roles: MANAGES, moduleKey: 'kpi-scoring',
    why: 'Your rating and the weights produce the final score, which sets the calibration rating and the annual bonus tier.',
    steps: [
      'Open KPI scoring and pick someone whose self-evaluation is in.',
      'Rate each KPI 1–5, then finalise.',
      'Write the narrative review under Performance reviews, including a calibration rating and summary notes.',
    ],
  },
  {
    id: 'cycle-eligibility', phase: 'cycle', title: 'Excuse someone from a cycle',
    roles: MANAGES, moduleKey: 'reviews',
    why: 'A new hire, someone on extended leave, or a leaver should not be measured over a period they were not there for — and should not have it averaged into their annual bonus.',
    steps: [
      'On Performance reviews, find them in “Start or continue a review”.',
      'Click “Not eligible this cycle” and give a reason.',
      'They are then excluded from the review, KPI scoring, reminder emails, and annual bonus averaging for that cycle.',
      'Existing reviews and scores are kept, and you can reverse it at any time.',
    ],
  },
  {
    id: 'declare-champion', phase: 'cycle', title: 'Declare a quarterly champion',
    roles: MANAGES, moduleKey: 'kpi-scoring',
    why: 'Recognition for the cycle, separate from the annual bonus. Eligibility and the reward amount come from the role type.',
    steps: [
      'Open KPI scoring and go to the Quarterly champions tab.',
      'Pick the cycle to see final scores for everyone whose KPIs are finalised.',
      'Declare the champion — only roles marked champion-eligible appear.',
    ],
  },
  {
    id: 'close-cycle', phase: 'cycle', title: 'Close the cycle',
    roles: ['admin', 'dept_head'], moduleKey: 'review-cycles',
    why: 'Closing a Tech & Product cycle FREEZES its reviews and KPI ratings as historical record. Sales cycles do not lock. An admin can re-open a cycle by setting it back to active.',
    steps: [
      'Open Review cycles and set the cycle to Closed.',
      'On cycle 3 (the annual one), generate annual bonuses from KPI scoring afterwards.',
      'Creating the next cycle carries these plans and weights forward automatically.',
    ],
  },

  // ── ANYTIME ──────────────────────────────────────────────────────────────
  {
    id: 'set-okrs', phase: 'ongoing', title: 'Set objectives and key results',
    roles: ALL, moduleKey: 'okrs',
    why: 'OKRs are what you are trying to achieve; KPIs are how you are measured. They are tracked per cycle but not scored.',
    steps: [
      'Open OKRs & goals and add an objective with a due date and key results.',
      'Update progress as you go — org-wide goals are visible to everyone but admin-only to edit.',
    ],
  },
  {
    id: 'run-checkins', phase: 'ongoing', title: 'Run 1:1 check-ins',
    roles: ALL, moduleKey: 'checkins',
    why: 'Regular conversations are where performance is actually managed. The formal review should hold no surprises.',
    steps: [
      'Open Check-ins to see what is scheduled, completed, or overdue.',
      'Managers schedule a check-in or a review session, set an agenda, then record notes afterwards.',
    ],
  },
  {
    id: 'give-feedback', phase: 'ongoing', title: 'Give 360 feedback',
    roles: ALL, moduleKey: 'feedback',
    why: 'Peer, manager, and upward feedback adds perspective a single manager cannot see.',
    steps: [
      'Open 360 feedback and click + Give feedback.',
      'Anyone can give feedback to anyone. Anonymous feedback hides your name from the recipient, though admins can still see it.',
    ],
  },
  {
    id: 'career-ladder', phase: 'ongoing', title: 'Track career progression',
    roles: ALL, moduleKey: 'career',
    why: 'A development plan turns review feedback into specific, dated actions.',
    steps: [
      'Open Career ladder to see the track and level.',
      'Managers add development plan items with a category and a due date, then mark them complete.',
    ],
  },
  // ── Sales-only. Program gating is DERIVED from these modules' programId,
  //    so these appear only for Sales users without being flagged here.
  {
    id: 'sales-action-plan', phase: 'ongoing', title: 'Fill in your action plan',
    roles: ALL, moduleKey: 'sales-action-plans',
    why: 'Documents what activity, and what level of it, drives your results — one row per month of the cycle.',
    steps: [
      'Open Action plans and pick the cycle.',
      'For each month, fill in targets, pipeline management, activity commitment, and the support you need.',
    ],
  },
  {
    id: 'sales-journal', phase: 'ongoing', title: 'Write your weekly journal',
    roles: ALL, moduleKey: 'sales-journal',
    why: 'A short weekly record of progress, reflections, and learnings. Past weeks lock, so a week you skip cannot be filled in later.',
    steps: [
      'Open Weekly journal — the current week is the one you can edit.',
      'Record what got done, what is and is not working, and what you learned.',
    ],
  },
  {
    id: 'sales-gas', phase: 'ongoing', title: 'Run a Group Alignment Session',
    roles: ALL, moduleKey: 'sales-gas',
    why: 'A shared multi-participant session tied to a cycle, with one agenda and one set of notes.',
    steps: [
      'Open GAS sessions and schedule one, adding participants.',
      'Capture notes during or after; participants can view and contribute.',
    ],
  },
];

const BY_ID = new Map(TASKS.map(t => [t.id, t]));

export function taskById(id) {
  return BY_ID.get(id);
}

/** The registry module a task is performed in — the source of its label and route. */
export function moduleForTask(task) {
  return moduleByKey(task.moduleKey);
}

/**
 * Tasks this user should see: role match, plus the program gating of the module
 * the task lives in. Program gating is derived, never restated on the task.
 */
export function visibleTasks(user, activeProgramId) {
  const role = user?.role;
  const program = Number(activeProgramId) || Number(user?.program_id) || null;
  if (!role) return [];
  return TASKS.filter(t => {
    if (!t.roles.includes(role)) return false;
    const mod = moduleByKey(t.moduleKey);
    if (mod.programId == null) return true;
    return Number(mod.programId) === program;
  });
}

/** visibleTasks grouped into PHASES order, dropping phases with nothing in them. */
export function visibleTaskPhases(user, activeProgramId) {
  const tasks = visibleTasks(user, activeProgramId);
  return PHASES
    .map(p => ({ ...p, tasks: tasks.filter(t => t.phase === p.key) }))
    .filter(p => p.tasks.length > 0);
}
